import type { FC, ReactNode } from "react";
import {
  email,
  maxLength,
  minLength,
  number,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  useNotify,
} from "react-admin";
import {
  Badge,
  Box,
  Divider,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import {
  AlternateEmail,
  Badge as BadgeIcon,
  LockOutlined,
  PersonOutline,
  PhoneIphone,
  PublicOutlined,
  ShieldOutlined,
} from "@mui/icons-material";
import { TSupplierRole, getSupplierRoles } from "@/api";
import useSWR from "swr";
import { phonePrefixChoices } from "@/config/country";
import {
  PHONE_MAX_LENGTH,
  PHONE_MIN_LENGTH,
} from "@/resources/shared/available-user";

const UserFormToolbar = () => (
  <Toolbar
    sx={{
      px: 0,
      pt: 3,
      pb: 0,
      mt: 1,
      background: "transparent",
      justifyContent: "flex-end",
    }}
  >
    <SaveButton
      icon={<></>}
      label="Guardar usuario"
      sx={{
        px: 3.5,
        py: 1.1,
        borderRadius: 999,
        fontWeight: 600,
        textTransform: "none",
        boxShadow: "0 6px 20px -8px rgba(99, 102, 241, 0.55)",
      }}
    />
  </Toolbar>
);

type UserFormProps = {
  onSubmit: (values: any) => Promise<void>;
  supplierId: string;
  // When true the form is editing an existing user. Password is always
  // optional now (both create and edit) — leave blank to keep it unchanged
  // on edit, or to let the backend auto-generate one on create.
  isEdit?: boolean;
};

const startAdornment = (icon: ReactNode) => ({
  startAdornment: (
    <InputAdornment position="start" sx={{ color: "text.disabled", mr: 0.5 }}>
      {icon}
    </InputAdornment>
  ),
});

// Theme-aware so the form is legible in both light and dark mode. The
// previous version hardcoded near-white backgrounds, which collapsed onto
// the dark dialog as illegible gray blocks with white labels.
const inputSx = (theme: import("@mui/material").Theme) => {
  const dark = theme.palette.mode === "dark";
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      backgroundColor: dark
        ? "rgba(255, 255, 255, 0.04)"
        : "rgba(248, 250, 252, 0.65)",
      transition: "background-color 160ms ease, box-shadow 160ms ease",
      "&:hover": {
        backgroundColor: dark
          ? "rgba(255, 255, 255, 0.07)"
          : "rgba(241, 245, 249, 0.9)",
      },
      "&.Mui-focused": {
        backgroundColor: dark ? "rgba(255, 255, 255, 0.09)" : "#fff",
        boxShadow: `0 0 0 4px ${
          dark ? "rgba(129, 140, 248, 0.18)" : "rgba(99, 102, 241, 0.12)"
        }`,
      },
    },
    "& .MuiFormLabel-root": { fontWeight: 500 },
  };
};

const SectionHeader: FC<{
  step: number;
  title: string;
  hint: string;
}> = ({ step, title, hint }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{ mb: 2.5, mt: 0.5 }}
  >
    <Badge
      color="primary"
      badgeContent={step}
      sx={{
        "& .MuiBadge-badge": {
          position: "relative",
          transform: "none",
          fontWeight: 700,
          fontSize: 11,
          minWidth: 22,
          height: 22,
          borderRadius: 999,
        },
      }}
    />
    <Box>
      <Typography
        variant="overline"
        sx={{
          letterSpacing: "0.14em",
          fontWeight: 700,
          color: "text.primary",
          lineHeight: 1.1,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="caption"
        sx={{ display: "block", color: "text.secondary", mt: 0.25 }}
      >
        {hint}
      </Typography>
    </Box>
  </Stack>
);

const FieldRow: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
      gap: 2.25,
    }}
  >
    {children}
  </Box>
);

export const UserForm: FC<UserFormProps> = ({
  onSubmit,
  supplierId,
  isEdit,
}) => {
  const notify = useNotify();

  const { data: rolesData } = useSWR(
    supplierId ? ["supplier-roles", supplierId] : null,
    () => getSupplierRoles(supplierId, { page: 0, size: 1000 }),
    {
      revalidateOnFocus: false,
    }
  );

  const rolesChoices =
    rolesData?.content?.map((role: TSupplierRole) => ({
      id: role.id,
      name: role.name,
    })) || [];

  const handleSubmit = async (values: any) => {
    try {
      await onSubmit(values);
    } catch (error: any) {
      notify(error?.message || "Error al crear el usuario.", {
        type: "warning",
      });
    }
  };

  return (
    <SimpleForm
      onSubmit={handleSubmit}
      toolbar={<UserFormToolbar />}
      mode="onChange"
      warnWhenUnsavedChanges
      sx={{
        p: 0,
        "& .MuiCardContent-root": { p: 0 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Box>
          <SectionHeader
            step={1}
            title="Identidad"
            hint="Cómo aparecerá esta persona dentro del proveedor."
          />
          <FieldRow>
            <TextInput
              source="firstName"
              label="Nombre"
              validate={required()}
              fullWidth
              variant="outlined"
              sx={inputSx}
              InputProps={startAdornment(<PersonOutline fontSize="small" />)}
            />
            <TextInput
              source="lastName"
              label="Apellido"
              fullWidth
              variant="outlined"
              sx={inputSx}
              InputProps={startAdornment(<BadgeIcon fontSize="small" />)}
            />
          </FieldRow>
        </Box>

        <Divider flexItem sx={{ borderColor: "divider" }} />

        <Box>
          <SectionHeader
            step={2}
            title="Contacto y acceso"
            hint="Credenciales y datos para iniciar sesión."
          />
          <Stack spacing={2.25}>
            <FieldRow>
              <TextInput
                source="email"
                label="Correo electrónico"
                validate={[required(), email()]}
                parse={(v: string) => v?.toLowerCase()}
                fullWidth
                variant="outlined"
                sx={inputSx}
                InputProps={startAdornment(
                  <AlternateEmail fontSize="small" />
                )}
              />
              <SelectInput
                source="roleId"
                choices={rolesChoices}
                validate={required()}
                label="Rol"
                fullWidth
                variant="outlined"
                sx={inputSx}
                emptyText="Selecciona un rol"
              />
            </FieldRow>
            <FieldRow>
              <SelectInput
                source="phonePrefix"
                choices={phonePrefixChoices}
                validate={required()}
                label="País"
                fullWidth
                variant="outlined"
                sx={inputSx}
              />
              <TextInput
                source="phone"
                label="Teléfono"
                validate={[
                  required(),
                  number("Solo números"),
                  minLength(PHONE_MIN_LENGTH),
                  maxLength(PHONE_MAX_LENGTH),
                ]}
                inputMode="tel"
                fullWidth
                variant="outlined"
                sx={inputSx}
                InputProps={startAdornment(<PhoneIphone fontSize="small" />)}
              />
            </FieldRow>
            <TextInput
              source="password"
              label="Contraseña"
              type="password"
              fullWidth
              variant="outlined"
              sx={inputSx}
              InputProps={startAdornment(<LockOutlined fontSize="small" />)}
              helperText={
                isEdit
                  ? "Opcional · déjalo vacío para no cambiar la contraseña actual."
                  : "Opcional · si lo dejas vacío se generará una automáticamente."
              }
            />
          </Stack>
        </Box>

        <Box
          sx={{
            mt: 0.5,
            px: 2.25,
            py: 1.5,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            backgroundColor: "rgba(99, 102, 241, 0.06)",
            border: "1px solid rgba(99, 102, 241, 0.16)",
            color: "text.secondary",
          }}
        >
          <ShieldOutlined fontSize="small" sx={{ color: "primary.main" }} />
          <Typography variant="caption" sx={{ lineHeight: 1.5 }}>
            {isEdit
              ? "Los cambios se aplican de inmediato. Solo escribe una contraseña si quieres reemplazar la actual."
              : "El acceso queda listo al guardar. El usuario puede iniciar sesión con su correo y la contraseña que defina aquí o la que reciba por correo."}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <PublicOutlined sx={{ color: "text.disabled" }} fontSize="small" />
        </Box>
      </Box>
    </SimpleForm>
  );
};
